// src/pages/PublicCombos.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaCalendarCheck, FaChevronRight, FaHandshake, FaMapMarkerAlt, FaPercent, FaSearch, FaShieldAlt, FaStar, FaStore } from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { comboAction, formatMoney } from '../lib/combos';

type Combo = {
  id: string;
  slug: string;
  title: string;
  description: string;
  region?: string;
  city?: string;
  category?: string;
  image_url?: string;
  suggested_price?: number;
  total_value?: number;
  commission_rate?: number;
  participants_count?: number;
  next_slots_count?: number;
};

export default function PublicCombos() {
  const [loading, setLoading] = useState(true);
  const [combos, setCombos] = useState<Combo[]>([]);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');

  useEffect(() => {
    let mounted = true;
    comboAction<{ combos: Combo[] }>('list_public')
      .then((res) => { if (mounted) setCombos(res.combos || []); })
      .catch((err) => console.error('Combos publics:', err))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const categories = useMemo(() => ['all', ...Array.from(new Set(combos.map(c => c.category).filter(Boolean)))], [combos]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return combos.filter(c => {
      const haystack = `${c.title} ${c.description} ${c.region} ${c.city} ${c.category}`.toLowerCase();
      return (!q || haystack.includes(q)) && (category === 'all' || c.category === category);
    });
  }, [combos, query, category]);

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Header />
      <section className="relative overflow-hidden pt-28 pb-16">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,.25),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,.25),transparent_35%)]" />
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/30 bg-cyan-300/10 px-4 py-2 text-xs font-black uppercase tracking-[0.22em] text-cyan-100 mb-5">
              <FaHandshake /> Combos Forfeo
            </div>
            <h1 className="text-4xl md:text-7xl font-black tracking-tight leading-[.95]">
              Plusieurs marchands. <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-300">Un seul moment.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-300 max-w-3xl leading-relaxed">
              Les Combos Forfeo regroupent des entreprises locales qui acceptent ensemble un prix, des disponibilités et une expérience coordonnée. Tu choisis une plage horaire simple, Forfeo s’occupe de la coordination numérique.
            </p>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="rounded-3xl border border-white/10 bg-white/10 p-5"><FaStore className="text-cyan-300 mb-3" /> 2 à 3 entreprises locales</div>
              <div className="rounded-3xl border border-white/10 bg-white/10 p-5"><FaPercent className="text-cyan-300 mb-3" /> Prix combo proposé par Forfeo</div>
              <div className="rounded-3xl border border-white/10 bg-white/10 p-5"><FaCalendarCheck className="text-cyan-300 mb-3" /> Plage horaire bloquée</div>
            </div>
          </div>
        </div>
      </section>

      <main className="bg-slate-50 text-slate-900 rounded-t-[3rem] -mt-6 relative z-10 py-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm p-4 md:p-5 flex flex-col md:flex-row gap-3 mb-8">
            <div className="flex-1 flex items-center gap-3 bg-slate-50 rounded-2xl px-4 py-3 border border-slate-100">
              <FaSearch className="text-slate-400" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher : spa, hôtel, famille, Québec..." className="bg-transparent outline-none w-full font-semibold" />
            </div>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="rounded-2xl border border-slate-200 px-4 py-3 font-black bg-white">
              {categories.map(c => <option key={c} value={c}>{c === 'all' ? 'Toutes les catégories' : c}</option>)}
            </select>
            <Link to="/business-dashboard?tab=combos" className="rounded-2xl bg-slate-900 px-5 py-3 text-white font-black text-center">Créer un Combo marchand</Link>
          </div>

          {loading ? <div className="p-10 text-center font-black">Chargement des Combos...</div> : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filtered.map(combo => (
                <Link key={combo.id} to={`/combos/${combo.slug || combo.id}`} className="group bg-white rounded-[2rem] border border-slate-200 overflow-hidden shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all">
                  <div className="h-52 bg-gradient-to-br from-slate-900 to-blue-800 relative overflow-hidden">
                    {combo.image_url ? <img src={combo.image_url} alt={combo.title} className="h-full w-full object-cover group-hover:scale-105 transition duration-700" /> : null}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-white">
                      <span className="rounded-full bg-white/15 border border-white/20 px-3 py-1 text-xs font-black backdrop-blur">{combo.category || 'Combo'}</span>
                      <span className="rounded-full bg-emerald-400 text-emerald-950 px-3 py-1 text-xs font-black">Actif</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-black group-hover:text-blue-700 transition">{combo.title}</h3>
                    <p className="text-sm text-slate-600 mt-2 line-clamp-3">{combo.description}</p>
                    <div className="mt-5 flex items-center gap-2 text-xs font-black text-slate-500 uppercase tracking-widest"><FaMapMarkerAlt /> {combo.city || combo.region || 'Québec'}</div>
                    <div className="mt-5 grid grid-cols-3 gap-2 text-center">
                      <div className="rounded-2xl bg-slate-50 p-3"><p className="text-[10px] uppercase font-black text-slate-400">Prix</p><p className="font-black">{formatMoney(combo.suggested_price)}</p></div>
                      <div className="rounded-2xl bg-slate-50 p-3"><p className="text-[10px] uppercase font-black text-slate-400">Valeur</p><p className="font-black">{formatMoney(combo.total_value)}</p></div>
                      <div className="rounded-2xl bg-slate-50 p-3"><p className="text-[10px] uppercase font-black text-slate-400">Marchands</p><p className="font-black">{combo.participants_count || 0}</p></div>
                    </div>
                    <div className="mt-5 flex items-center justify-between font-black text-blue-700">Voir le Combo <FaChevronRight /></div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="bg-white rounded-[2rem] p-10 text-center border border-slate-200">
              <FaStar className="mx-auto text-slate-300 text-4xl mb-4" />
              <h3 className="text-2xl font-black">Aucun Combo actif pour l’instant</h3>
              <p className="text-slate-500 mt-2">Les marchands peuvent créer leurs premiers Combos depuis leur dashboard.</p>
            </div>
          )}

          <div className="mt-10 rounded-[2rem] border border-blue-100 bg-blue-50 p-6 flex gap-4 text-blue-950">
            <FaShieldAlt className="text-blue-600 shrink-0 mt-1" />
            <p className="text-sm leading-relaxed"><strong>Important :</strong> Forfeo agit comme plateforme technologique de mise en relation, de visibilité, de coordination commerciale et de paiement. Chaque entreprise participante demeure responsable de sa portion du service.</p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
